import { ErrorCode } from 'react-dropzone';
import { NotificationCanal, NotificationPriority, NotificationType } from './notifications';
import { Upload } from 'graphql-upload';
import { BusinessType, CRAStatus, IBloomies, PurchaseOrder } from './domain';

export type SupplierInvoiceStatus = 'created' | 'imported' | 'errored' | 'refused';

export type ContractLegalStatus = 'pending' | 'on going' | 'done' | 'cancelled' | 'refused';

export const AddressCountryCodeEU = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
];

export enum ATSOrigin {
  pipefy = 'pipefy',
  hiresweet = 'hiresweet',
  airtable = 'airtable',
}

export interface BrowserUploadFile extends Partial<File> {
  url?: string;
  path?: string;
  errors?: { message: string; code: ErrorCode | string }[];
}

export type DataValue = {
  firstname: string;
  lastname: string;
  email: string;
  jobTitle: string;
  linkedIn?: string;
};

export enum ActivityDayType {
  'nbDaysWorked' = 'nbDaysWorked',
  'nbDaysMarkupWorked' = 'nbDaysMarkupWorked',
  'nbDaysMarkup2Worked' = 'nbDaysMarkup2Worked',
  'astreinte' = 'astreinte',
}

export type ActivityDayDetails = {
  id: number;
  day: string;
  date: string;
  isHoliday: boolean;
  isSunday: boolean;
  isSaturday: boolean;
  isWeekEndDay: boolean;
  dayWorked?: number;
  checked: boolean;
  comment?: string;
  astreinte?: number;
};

export type ActivityNbDayDetails = {
  [key: number]: {
    duration: number;
    comment?: string;
    isAstreinte: number;
  };
};

export type DataCSV = {
  data: {
    line: number | string;
    firstname: string;
    lastname: string;
    email: string;
    jobTitle: string;
    linkedIn?: string | undefined;
  };
};

export interface ITag {
  value: string;
  count?: number;
}

export type Paginate = {
  take: number;
  skip: number;
};

export type Item = {
  id: string;
  minLevel: number;
  label: string;
};

// ------------ api.bloomers -------------

export interface IMissionQueryInput {
  research?: string;
}

export type BrowserFieldOrFile = BrowserUploadFile | string;
export type ServerFieldOrFile = Upload | string;

export type BloomerFiles = 'credentialID';

export interface IBloomer {
  isNew?: boolean;
  id?: string;
  fullname?: string;
  firstname: string;
  lastname: string;
  email: string;
  secondaryEmail?: string;
  phoneNumber?: string;
  slack?: boolean;
  accountManagerId?: string;
  accountManagerName?: string;
  accountManager?: IAccountManager;
  missionRecordId?: string[];
  mission?: IMission[];
  recordId?: string;
  credentialID?: string;
  invitationRecordId?: string[];
  invitation?: Invitation[];
  communicationType: NotificationCanal;
  hiresweetContactId?: string;
}

// carefull this interface is for client side only
export interface BrowserBloomer extends Omit<IBloomer, 'credentialID' | 'invitation'> {
  credentialID?: BrowserFieldOrFile;
  invitation?: Invitation;
}

export interface IAccountManager extends IBloomies {
  id?: string;
  fullname?: string;
  recordId?: string;
  /** Liens Airtable vers fiches bloomers (résolveur admin). */
  bloomers?: string[];
}

export interface IMission {
  contracts?: string[];
  enableCRAUpload: boolean;
  jobId?: string;
  purchaseOrderRequired?: boolean;
  havePreviousMonthActivity?: string[] | undefined;
  id?: string;
  label?: string;
  beginMission: string;
  endMission: string;
  statusBloomer?: string;
  statusClient?: string;
  activityRecordId: string[];
  activity?: IActivity[];
  pricingRecordId: string[];
  pricing?: IPricing[];
  clientSocietyRecordId?: string;
  clientSociety?: ISociety;
  clientBillingSocietyRecordId?: string;
  clientBillingSociety?: ISociety;
  bloomerBillingSocietyRecordId: string[];
  bloomerBillingSociety?: ISociety[];
  contactOperationsRecordId?: string;
  contactOperations?: IContact;
  clientLegalContactsRecordId?: string[];
  clientLegalContacts?: IContact[];
  bloomerLegalContactsRecordId?: string[];
  bloomerLegalContacts?: IContact[];
  billingContactsRecordId: string[];
  billingContacts?: IContact[];
  recordId?: string;
  isActive: boolean;
  externalId?: string;
  bloomerSocietyName: string[];
  clientSocietyName: string[];
  clientBillingSocietyName?: string[];
  bloomerRecordId: string[];
  bloomerFullName?: string[];
  bloomerPaydateDays?: number;
  clientPaydateDays?: number;
  type: string;
  reasearchField?: string;
  CSMRecordId: string[];
  CSM?: IAccountManager;
  accountManager?: IAccountManager[];
  resolvedClientPdsContact?: ResolvedContact;
  resolvedClientBillingContacts?: ResolvedContact[];
  resolvedSupplierSstContact?: ResolvedContact;
  bloomer?: BrowserBloomer;
  contractType?: string;
  createdAt?: string;
  replacement: boolean;
  shouldBeCountAsDeal: boolean;
  hoursPerDays: number;
  origin?: ATSOrigin;
  enableBusinessReferalNpsClient: boolean;
  enableBusinessReferalNpsBloomer: boolean;
  modifiedBy?: { name: string };
  suiviCardId?: string;
  purchaseOrderRecordId?: string[];
  /**
   * Code mission repris dans les bons de commande client (souvent le trigramme du Bloomer,
   * ex. « ACH » pour Abdelkarim Chtif). Saisie libre, sans contrainte de format.
   */
  missionCode?: string;
}

export type SocietyFiles = 'KBIS' | 'URSSAFVigilanceCertificate' | 'RCPVigilanceCertificate';

export interface ISociety {
  id?: string;
  name: string;
  status?: string;
  siret: string;
  isClient?: boolean;
  bloomers?: string[];
  billingContactsRecordId?: string[];
  billingContacts?: IContact[];
  legalContactsRecordId?: string[];
  legalContacts?: IContact[];
  missionRecordId?: string[];
  mission?: IMission[];
  bloomerBillingMissionRecordId?: string[];
  bloomerBillingMission?: IMission[];
  recordId?: string;
  KBIS?: string;
  KbisExpirationDate?: string;
  URSSAFVigilanceCertificate?: string;
  URSSAFVCExpirationDate?: string;
  URSSAFVCUploadDate?: string;
  RCPVigilanceCertificate?: string;
  RCPExpirationDate?: string;
  IBAN?: string;
  BIC?: string;
  portage?: boolean;
  paydateDays?: number;
  tva?: string;
  addressName?: string;
  addressPart1?: string;
  addressPart2?: string;
  addressZip?: string;
  addressTown?: string;
  addressCountryCode?: string | null;
  addressCountry?: string;
  externalId?: string;
  pitch?: string;
  description?: string;
  interviewTips?: string;
  internalOrganization?: string;
  phone?: string;
  autoValidateInvoice?: boolean;
  orderNumberRequired?: boolean;
  pennylaneId?: string;
  purchaseOrderRecordId?: string;
}

export interface IPricing {
  id?: string;
  startDate?: Date;
  purchasePricePerDayWithoutTaxes: number;
  salePricePerDayWithoutTaxes: number;
  marginAmount?: string;
  marginRate?: string;
  taxes: number;
  recordId?: string;
  astreinteCoef?: number;
  recordIdMission: string[];
  activityRecordId?: string[];
  activity?: IActivity[];
  mission?: IMission[];
}

export type ActivityFiles = 'urlCraS3' | 'urlInvoicesS3' | 'urlAdditionalInvoicesS3';

export interface IActivity {
  jobId?: string;
  id?: string;
  month: string;
  nbDaysWorked: number;
  nbDaysMarkupWorked: number;
  nbDaysMarkup2Worked: number;
  nbDaysDetails: string;
  astreinte: number;
  validated: boolean;
  /**
   * Domaine / GraphQL : indique un flux de signature en cours (non persisté Airtable).
   * Renseigné par l’API (ex. `normalizeActivityForGraphql` dans `api.bloomers`) ; le front s’appuie sur ce champ, pas sur une fonction utilitaire partagée.
   */
  signing?: boolean;
  recordId: string;
  signingDocumentId?: string;
  billed: boolean;
  signedCount: number;
  urlCraS3?: string;
  urlInvoicesS3?: string;
  urlAdditionalInvoicesS3?: string;
  bloomerFullname: string[];
  clientMission: string[];
  bloomerRecordId: string[];
  type?: string[];
  signingEmbedBloomerId?: string;
  signingEmbedClientId?: string;
  recordIdMission: string;
  mission?: IMission;
  clientSocietyName: string;
  payed: boolean;
  dateUploadInvoice?: string;
  CRAStatus: CRAStatus;
  lastError?: string;
  lastSupplierError?: string;
  externalCustomerInvoiceId?: string;
  externalCustomerInvoiceMarkupId?: string;
  externalSupplierInvoiceId?: string;
  supplierInvoiceStatus?: SupplierInvoiceStatus;
  hoursPerDays: number;
  /** Numéro de BDC retenu à la facturation (dénormalisé). */
  orderNumber?: string;
}

export interface IContact {
  id?: string;
  fullname?: string;
  firstname: string;
  lastname: string;
  phoneNumber?: string;
  email: string;
  nameJob: string;
  isBilling: boolean;
  isOperational?: boolean | null;
  societyRecordId?: string[];
  society?: ISociety[];
  missionRecordId?: string[];
  mission?: IMission[];
  billingRecordId?: string[];
  billingContactMissionsRecordId?: string[];
  legalClientMissionsRecordId?: string[];
  legalBloomerMissionsRecordId?: string[];
  legalSocietiesRecordId?: string[];
  recordId?: string;
  externalId: string;
  communicationType: NotificationCanal;
}

export type ResolvedContact = {
  contact: IContact;
  source: string;
};

/** Rôle d'un contact rattaché à une société. */
export type ContactSocietyRole = 'billing' | 'legal';
/** Rôle d'un contact rattaché à une mission (billing = client uniquement au niveau mission). */
export type ContactMissionRole = 'billing' | 'legal' | 'operational';
/** Côté d'un contact (fournisseur / client). */
export type ContactSide = 'client' | 'supplier';
/** Rôle d'une société rattachée à une mission. */
export type SocietyMissionRole = 'clientPrincipal' | 'clientBilling' | 'bloomerBilling';

export interface IContractFile {
  id: string;
  url: string;
  filename: string;
  size: number;
  type: 'application/pdf';
}

export interface ILegalContract {
  recordId: string;
  id?: number;
  nbSigner: number;
  signerDocId1?: string;
  createdDate: string;
  createdAt: string;
  signedCount?: number;
  signedDate?: string;
  smartFields?: string;
  status: ContractLegalStatus;
  provider?: string;
  worflowExternalId?: string;
  contractTemplate?: string[];
  type: TemplateContract;
  step: string;
  emptyFile?: IContractFile[];
  signedFile?: IContractFile[];
  externalDocumentId?: string;
  signLink?: string;
  refusedReason?: string;
  /** ISO date of the last successful manual signature relaunch; drives the 24h anti-spam cooldown. */
  lastManualRelaunchAt?: string;
  /**
   * Who cancelled the signature request: the acting employee's display name, or
   * {@link ESIGN_WEBHOOK_CANCELLATION_ACTOR} when the cancellation came from the provider.
   */
  cancelledBy?: string;
  /** ISO date of the cancellation. */
  cancelledAt?: string;
  mission: string[];
  billing?: string[];
}

export type MissionContractType = 'bloomaysContract' | 'devis' | 'clientContract' | 'BDC';

export interface IMissionCardDetails {
  label?: string;
  recordId: string;
  externalId?: string;
  beginMission: string;
  endMission: string;
  purchasePricePerDayWithoutTaxes?: number;
  salePricePerDayWithoutTaxes?: number;
  taxes?: number;
  CSM?: string;
  contractType?: MissionContractType;
  replacement: boolean | null;
  shouldBeCountAsDeal: boolean;
  isActive: boolean;
  bloomerPaydateDays?: number;
  clientPaydateDays?: number;
  type: BusinessType;
  prices?: IMissionCardPricing[];
  clientBillingSociety?: ISociety;
  clientSociety?: ISociety;
  pricing?: IPricing;
  bloomerBillingSociety?: ISociety[];
  contactOperations?: IContact;
  billingContacts?: IContact;
  bloomer: IBloomer;
  hoursPerDays: number;
  jobId?: string;
  origin?: ATSOrigin;
  enableCRAUpload?: boolean;
  enableBusinessReferalNpsClient: boolean;
  enableBusinessReferalNpsBloomer: boolean;
  /** Flag « BDC requis » au niveau mission (lecture seule depuis Airtable). */
  purchaseOrderRequired?: boolean;
  /**
   * Flag effectif « BDC requis » résolu côté API : true si le flag mission ou
   * le flag client (société de facturation) est activé.
   */
  effectivePurchaseOrderRequired?: boolean;
  /** Historique des BDC liés à la mission (chargé via le résolveur GraphQL `IMission.purchaseOrders`). */
  purchaseOrders?: PurchaseOrder[];
  /**
   * Code mission repris dans les bons de commande client (souvent le trigramme du Bloomer,
   * ex. « ACH » pour Abdelkarim Chtif). Saisie libre, sans contrainte de format.
   */
  missionCode?: string;
}

export interface IMissionCardPricing {
  startDate?: Date;
  purchasePricePerDayWithoutTaxes: number;
  salePricePerDayWithoutTaxes: number;
  taxes: number;
  recordId?: string;
  astreinteCoef?: number;
  isCurrent?: boolean;
}

export type InvitationStatus = 'on going' | 'done' | 'not started';

export type Invitation = {
  recordId: string;
  token: string;
  jobTitle: string;
  clientSocietyName?: string;
  purchasePricePerDayWithoutTaxes?: number;
  missionStartDate?: Date;
  missionEndDate?: Date;
  firstname: string;
  lastname: string;
  missionRecordId?: string;
  status: InvitationStatus;
  isNew?: boolean;
  bloomerRecordId?: string[];
};

export interface ISorting {
  field: string;
  direction: string;
}

export interface IPagination {
  limit: number;
  offset: number;
}

export interface Recommendations {
  name: string;
  comment: string;
}

export interface IEducations {
  title: string;
  degree: string;
  url: string;
  description: string;
  fieldOfStudy: string;
  beginDate: Date;
  endDate: Date;
}

export interface IExperiences {
  title: string;
  description: string;
  companyName: string;
  url: string;
  beginDate: Date;
  endDate: Date;
}

export interface HardSkills {
  name: string;
  version?: number;
  beginDate?: string;
}

export interface IMetadata {
  createdAt?: Date | string;
  updatedAt?: Date | string;
  owner?: string | null;
  alreadyBloomer?: boolean;
  superBloomer?: boolean;
}

export interface IPositions {
  title?: string;
  url?: string;
  description?: string;
  companyName?: string;
  date1?: string;
  date2?: string;
  roles?: {
    title?: string;
    description?: string;
    date1?: string;
    date2?: string;
  }[];
}

export interface ILinkedInProfile {
  contact: { type: string; values: string[]; links: string[] }[];
  profile: {
    name?: string;
    headline?: string;
    location?: string;
    summary?: string;
    connections?: string;
    followers?: string;
    positions?: IPositions[] | [];
    imageurl?: string;
    languages: { name: string }[];
  };
  languages: { name: string }[];
  positions?: IPositions[] | [];
  skills?: { title: string }[];
  educations?: IPositions[];
  recommendations?: { received: { text: string; name: string }[] };
}

export interface ILinkedInUrl {
  linkedInUrl: string;
  linkedInCredential: ILinkedInCredential;
}

export interface ILinkedInCredential {
  cookies?: string;
  email?: string;
  password?: string;
}

export enum SettingFinanceType {
  finance = 'finance',
}

export enum SettingFinanceName {
  pay = 'pay',
  rent = 'rent',
  stack = 'stack',
}

export enum FinanceType {
  pay = 'pay',
  rent = 'rent',
  stack = 'stack',
  CDI = 'CDI',
  RPO = 'RPO',
  freelance = 'freelance',
  IT = 'IT',
  other = 'other',
  People = 'People',
}

/** Ligne « setting finance » (cache calculs) — domaine, pas le format filaire Airtable. */
export interface IFinanceSetting {
  name: SettingFinanceName;
  type: SettingFinanceType;
  price: number;
  date: Date;
  recordId?: string;
}

/** Ligne finance Airtable côté domaine (upsert hors adaptateur typé). */
export interface IFinanceRecord {
  id?: number;
  recordId?: string;
  year?: number;
  date: string;
  financeType: FinanceType;
  price?: number;
  isExpense: boolean | null;
  isForecast?: boolean;
}

export type WinType =
  | 'Freelance Tech & Digital'
  | 'Freelance RPO'
  | 'Freelance Bloomays People'
  | 'CDI Tech & Digital'
  | 'CDI Bloomays People';

export interface INotification {
  channel?: string;
  userGroup?: string;
  recordId?: string;
  title?: string;
  description?: string;
  priority: NotificationPriority;
  read: boolean;
  canal: NotificationCanal;
  creationDate: string;
  contact: string | null;
  type: NotificationType;
  deleted: boolean;
  canDelete: boolean;
  sent: boolean;
  sentDate?: Date;
  societyRecordId?: string;
  activityRecordId?: string;
  bloomerRecordId?: string;
  missionRecordId?: string;
  link?: string;
  taskId?: string;
}

/** Champs pour les jobs worker notification (email/slack), alignés sur la ligne métier sans nommage Airtable. */
export type NotificationQueuePayloadBase = {
  recordId?: string;
  title?: string;
  description?: string;
  contact: string | null;
  type: NotificationType;
  priority: NotificationPriority;
  canal: NotificationCanal;
  link?: string;
  channel?: string;
  userGroup?: string;
  canDelete: boolean;
  taskId?: string;
};

export interface ILightSlackNotification
  extends Omit<
    INotification,
    'title' | 'priority' | 'read' | 'deleted' | 'creationDate' | 'sent' | 'type' | 'canDelete' | 'contact'
  > {
  userGroup: string;
  channel: string;
}

export interface NotificationJob {
  type: NotificationType;
}

export interface Preferences {
  communicationType: string;
}

export interface MissionFilter {
  recordId?: string;
  month?: string | Date;
  isMissingCRA?: boolean;
}

export interface TalentProcess {
  phase: string;
  salesPriceWithoutTaxes?: number;
  pluses?: string;
  emailResponseAddres?: string;
  emailMessagingAddress?: string;
  referenceAccepted?: string;
  noGoReason?: string;
  noGoExplanation?: string;
}

export interface ContactsInput {
  email: string;
  phoneNumber: string;
}

export enum TemplateContract {
  // sous-traitance
  SST = 'SST',
  // prestation de service
  PDS = 'PDS',
}

/** Répondant NPS côté mission (équivalent GraphQL `UserType`). */
export enum NpsRespondentType {
  client = 'client',
  bloomer = 'bloomer',
}

/** Score NPS mission (table CRM Airtable `FollowBloomers`, domaine Bloomers). */
export interface NPSScoring {
  recordId?: string;
  fullName: string;
  email: string;
  created?: Date;
  score: number;
  comment?: string;
  collectDate: string;
  source: string;
  activityId: string;
  type: NpsRespondentType;
}

/** Payload mutation GraphQL `missionFollowUp` / service métier. */
export interface NPSScoringInput {
  recordId?: string;
  fullName?: string;
  email?: string;
  score: number;
  comment?: string;
  collectDate: string;
  source?: string;
  activityId: string;
  /** Si absent, déduit du rôle utilisateur côté service. */
  type?: NpsRespondentType;
}

/** Création ou mise à jour d’un enregistrement NPS (Airtable : `recordId` = id ligne pour update). */
export type NPSScoringUpsertInput = Omit<NPSScoring, 'recordId'> & { recordId?: string };

export type ActivityFileFields = 'urlCraS3' | 'urlInvoicesS3' | 'urlAdditionalInvoicesS3';
